import { type PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import intl from 'i18n/intl';
import {
  type PinCodeMode,
  pinCodeModeDefault,
} from 'shared/preferences-constants';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import { regionifyUrl } from 'utils/regionifyUrl';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';
import { configService } from '../services/config.service';

type TAppsPrefGeneralSettingsState = {
  loading: boolean;
  hasErrors: boolean;
  innerSettings: { pinCodeMode: PinCodeMode };
  flushCacheLoading: boolean;
};

export const initialState: TAppsPrefGeneralSettingsState = {
  loading: false,
  hasErrors: false,
  innerSettings: { pinCodeMode: pinCodeModeDefault },
  flushCacheLoading: false,
};

export const flushOrganizationCache = createAppAsyncThunk(
  'appsPrefGeneralSettings/flushOrganizationCache',
  async (_, { getState }) => {
    const { organizationId, region } = getState().auth?.user ?? {};
    if (!organizationId || !region) return;
    const preferenceServiceUrl = regionifyUrl(
      configService.REACT_APP_PREFERENCE_SERVICE_URL,
      region
    );
    await api.post({
      endpoint: `${preferenceServiceUrl}organization/${organizationId}/flush-cache`,
      successMessage: (intl.messages as Record<string, string>)
        .clear_cache_success_message,
    });
  }
);

const slice = createSlice({
  name: 'appsPrefGeneralSettings',
  initialState,
  reducers: {
    fetchData: (state) => {
      isLoadingRequest(state);
    },
    fetchDataSuccess: (
      state,
      { payload }: PayloadAction<TAppsPrefGeneralSettingsState['innerSettings']>
    ) => {
      state.innerSettings = payload;
      finishedLoadingSuccess(state);
    },
    fetchDataFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(flushOrganizationCache.pending, (state) => {
        state.flushCacheLoading = true;
      })
      .addMatcher(
        isAnyOf(
          flushOrganizationCache.fulfilled,
          flushOrganizationCache.rejected
        ),
        (state) => {
          state.flushCacheLoading = false;
        }
      );
  },
});

export default slice.reducer;

// Selectors
export const PrefGeneralSettingsLoading = (state: TRootState) =>
  state.appsPrefGeneralSettings.loading;
export const PrefGeneralSettingsInnerSettings = (state: TRootState) =>
  state.appsPrefGeneralSettings.innerSettings;
export const PrefGeneralSettingsFlushCacheLoading = (state: TRootState) =>
  state.appsPrefGeneralSettings.flushCacheLoading;

// Actions
export const { fetchData, fetchDataSuccess, fetchDataFailure } = slice.actions;

export const getPreferencesGeneralSettings = createAppAsyncThunk(
  'appsPrefGeneralSettings/getPreferencesGeneralSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchData());
      const data = await api.get({
        endpoint: '/apps/TE_PREFERENCES/preferences-general-settings',
      });
      dispatch(fetchDataSuccess(data));
    } catch (e) {
      dispatch(fetchDataFailure());
      return console.error(e);
    }
  }
);

export const updatePreferencesGeneralSettings = createAppAsyncThunk<
  void,
  TAppsPrefGeneralSettingsState['innerSettings']
>(
  'appsPrefReviewSettings/updatePreferencesGeneralSettings',
  async (dataBody, { dispatch }) => {
    try {
      await api.patch({
        endpoint: '/apps/TE_PREFERENCES/preferences-general-settings',
        data: {
          ...dataBody,
          createdAt: undefined,
          updatedAt: undefined,
          _id: undefined,
          __v: undefined,
        },
        successMessage: 'Settings saved',
      });
      dispatch(getPreferencesGeneralSettings());
    } catch (e) {
      dispatch(fetchDataFailure());
      return console.error(e);
    }
  }
);
